import React, { useState, useEffect } from 'react';
import { 
  FileText, Save, Plus, Trash2, Link, Palette, Eye,
  Facebook, Twitter, Instagram, Youtube, Loader2, Check
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FooterSettings = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    text: '',
    links: [],
    show_social: false,
    social_links: {
      facebook: '',
      twitter: '',
      instagram: '',
      youtube: ''
    },
    background_color: '#f9fafb',
    text_color: '#6b7280',
    font_size: '14',
    font_family: 'Inter',
    text_alignment: 'center'
  });

  const fontFamilies = [
    { value: 'Inter', label: 'Inter (Default)' },
    { value: 'Arial', label: 'Arial' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Times New Roman', label: 'Times New Roman' },
    { value: 'Verdana', label: 'Verdana' },
    { value: 'Trebuchet MS', label: 'Trebuchet MS' },
    { value: 'Courier New', label: 'Courier New' },
  ];

  const fontSizes = ['10', '12', '14', '16', '18', '20', '24'];
  const alignments = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ];

  const token = localStorage.getItem('auth_token');

  // Fetch footer settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/footer`);
        if (res.ok) {
          const data = await res.json();
          setSettings({
            text: data.text || '',
            links: data.links || [],
            show_social: data.show_social || false,
            social_links: data.social_links || { facebook: '', twitter: '', instagram: '', youtube: '' },
            background_color: data.background_color || '#f9fafb',
            text_color: data.text_color || '#6b7280',
            font_size: data.font_size || '14',
            font_family: data.font_family || 'Inter',
            text_alignment: data.text_alignment || 'center'
          });
        }
      } catch (error) {
        console.error('Error fetching footer settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/footer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Footer settings saved successfully!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save footer settings');
    } finally {
      setSaving(false);
    }
  };

  // Add new link
  const addLink = () => {
    setSettings(prev => ({
      ...prev,
      links: [...prev.links, { label: '', url: '' }]
    }));
  };

  // Remove link
  const removeLink = (index) => {
    setSettings(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  // Update link
  const updateLink = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      links: prev.links.map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton onClick={onBack} />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-7 h-7 text-blue-600" />
                Footer Settings
              </h1>
              <p className="text-gray-500 text-sm">Customize the footer that appears on every page</p>
            </div>
          </div>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            data-testid="save-footer-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Footer
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Main Footer Text */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-600" />
            Footer Text
          </h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="footer-text">Main Text</Label>
              <Input
                id="footer-text"
                value={settings.text}
                onChange={(e) => setSettings(prev => ({ ...prev, text: e.target.value }))}
                placeholder="Enter footer text (e.g., © 2024 UPLANDS FOOD MARKET. All rights reserved.)"
                className="mt-1"
                data-testid="footer-text-input"
              />
              <p className="text-xs text-gray-500 mt-1">This text will appear in the footer of every page</p>
            </div>
          </div>
        </Card>

        {/* Footer Links */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Link className="w-5 h-5 text-gray-600" />
              Footer Links
            </h2>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={addLink}
              className="gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Link
            </Button>
          </div>
          
          {settings.links.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No links added yet. Click "Add Link" to create one.</p>
          ) : (
            <div className="space-y-3">
              {settings.links.map((link, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <Input
                      value={link.label}
                      onChange={(e) => updateLink(index, 'label', e.target.value)}
                      placeholder="Link Label (e.g., Privacy Policy)"
                    />
                    <Input
                      value={link.url}
                      onChange={(e) => updateLink(index, 'url', e.target.value)}
                      placeholder="URL (e.g., /privacy)"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLink(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Social Media Links */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Instagram className="w-5 h-5 text-gray-600" />
              Social Media Links
            </h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.show_social}
                onChange={(e) => setSettings(prev => ({ ...prev, show_social: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-600">Show social icons</span>
            </label>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2">
                <Facebook className="w-4 h-4 text-blue-600" />
                Facebook
              </Label>
              <Input
                value={settings.social_links.facebook || ''}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  social_links: { ...prev.social_links, facebook: e.target.value }
                }))}
                placeholder="https://facebook.com/yourpage"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Twitter className="w-4 h-4 text-sky-500" />
                Twitter
              </Label>
              <Input
                value={settings.social_links.twitter || ''}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  social_links: { ...prev.social_links, twitter: e.target.value }
                }))}
                placeholder="https://twitter.com/yourhandle"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-pink-600" />
                Instagram
              </Label>
              <Input
                value={settings.social_links.instagram || ''}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  social_links: { ...prev.social_links, instagram: e.target.value }
                }))}
                placeholder="https://instagram.com/yourprofile"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Youtube className="w-4 h-4 text-red-600" />
                YouTube
              </Label>
              <Input
                value={settings.social_links.youtube || ''}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  social_links: { ...prev.social_links, youtube: e.target.value }
                }))}
                placeholder="https://youtube.com/yourchannel"
                className="mt-1"
              />
            </div>
          </div>
        </Card>

        {/* Colors */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5 text-gray-600" />
            Footer Colors
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Background Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={settings.background_color}
                  onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={settings.background_color}
                  onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <Label>Text Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={settings.text_color}
                  onChange={(e) => setSettings(prev => ({ ...prev, text_color: e.target.value }))}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={settings.text_color}
                  onChange={(e) => setSettings(prev => ({ ...prev, text_color: e.target.value }))}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Typography */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-600" />
            Typography
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Font Family</Label>
              <select
                value={settings.font_family}
                onChange={(e) => setSettings(prev => ({ ...prev, font_family: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {fontFamilies.map(font => (
                  <option key={font.value} value={font.value}>{font.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Font Size (px)</Label>
              <select
                value={settings.font_size}
                onChange={(e) => setSettings(prev => ({ ...prev, font_size: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {fontSizes.map(size => (
                  <option key={size} value={size}>{size}px</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Text Alignment</Label>
              <select
                value={settings.text_alignment}
                onChange={(e) => setSettings(prev => ({ ...prev, text_alignment: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {alignments.map(align => (
                  <option key={align.value} value={align.value}>{align.label}</option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Preview */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-gray-600" />
            Preview
          </h2>
          <div 
            className="rounded-lg p-4 border"
            style={{ backgroundColor: settings.background_color }}
          >
            <div style={{ textAlign: settings.text_alignment }}>
              {settings.links.length > 0 && (
                <div className={`flex items-center gap-4 mb-3 flex-wrap ${
                  settings.text_alignment === 'center' ? 'justify-center' : 
                  settings.text_alignment === 'right' ? 'justify-end' : 'justify-start'
                }`}>
                  {settings.links.map((link, index) => (
                    <a
                      key={index}
                      href={link.url || '#'}
                      className="hover:underline"
                      style={{ 
                        color: settings.text_color,
                        fontFamily: settings.font_family,
                        fontSize: `${settings.font_size}px`
                      }}
                    >
                      {link.label || 'Link'}
                    </a>
                  ))}
                </div>
              )}
              
              {settings.show_social && (
                <div className={`flex items-center gap-4 mb-3 ${
                  settings.text_alignment === 'center' ? 'justify-center' : 
                  settings.text_alignment === 'right' ? 'justify-end' : 'justify-start'
                }`}>
                  {settings.social_links.facebook && (
                    <Facebook className="w-5 h-5" style={{ color: settings.text_color }} />
                  )}
                  {settings.social_links.twitter && (
                    <Twitter className="w-5 h-5" style={{ color: settings.text_color }} />
                  )}
                  {settings.social_links.instagram && (
                    <Instagram className="w-5 h-5" style={{ color: settings.text_color }} />
                  )}
                  {settings.social_links.youtube && (
                    <Youtube className="w-5 h-5" style={{ color: settings.text_color }} />
                  )}
                </div>
              )}
              
              <p style={{ 
                color: settings.text_color,
                fontFamily: settings.font_family,
                fontSize: `${settings.font_size}px`
              }}>
                {settings.text || 'Your footer text will appear here'}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FooterSettings;
